import * as React from "react";
import {
    Typography,
    Box,
    Button,
    IconButton,
    InputAdornment,
    useMediaQuery,
    useTheme,
    Chip,
    CircularProgress,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Rating,
    Collapse,
    Grid,
} from "@mui/material";
import { connect } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { main_button, MainInput, centered_flex_box } from "../app/components/Styles";
import CreateTicketModal from "../app/components/CreateTicketModal";
import { Table, Image } from "antd";
import { getRequest, postRequest } from "../core/network";
import endpoints from "../constants/endPoints.json";
import { useEffect } from "react";
import { Add, AddPhotoAlternate, ArrowBack, Close, FilterList, FilterListOff, Reply, Search } from "@mui/icons-material";
import NewCard from "../app/components/NewCard";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { notification } from "antd";
import SearchFilterBar, { FilterChips, plusIcon } from "../app/glass/SearchFilterBar";

const TICKET_TYPES = [
    { value: "General Inquiry", labelKey: "helpDesk.ticketTypeGeneralInquiry" },
    { value: "Order", labelKey: "helpDesk.ticketTypeOrder" },
    { value: "Product", labelKey: "helpDesk.ticketTypeProduct" },
    { value: "Inventory", labelKey: "helpDesk.ticketTypeInventory" },
    { value: "Finance", labelKey: "helpDesk.ticketTypeFinance" },
];

const STATUS_OPTIONS = [
    { value: "", labelKey: "common.all" },
    { value: "Pending", labelKey: "helpDesk.statusPending" },
    { value: "In Progress", labelKey: "helpDesk.statusInProgress" },
    { value: "Resolved", labelKey: "helpDesk.statusResolved" },
    { value: "Closed", labelKey: "helpDesk.statusClosed" },
];

export const HelpDesk = ({ token }) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const { t } = useTranslation();

    const [page, setPage] = React.useState(parseInt(searchParams.get("page")) || 1);
    const [pageSize, setPageSize] = React.useState(parseInt(searchParams.get("limit")) || 10);
    const [search, setSearch] = React.useState(searchParams.get("search") || "");
    const [statusFilter, setStatusFilter] = React.useState(searchParams.get("status") || "");
    const [ticketTypeFilter, setTicketTypeFilter] = React.useState(searchParams.get("ticketType") || "");
    const [tickets, setTickets] = React.useState(null);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState(null);

    const ticketId = searchParams.get("ticketId");
    const [ticketDetail, setTicketDetail] = React.useState(null);
    const [comments, setComments] = React.useState([]);
    const [feedback, setFeedback] = React.useState(null);
    const [detailLoading, setDetailLoading] = React.useState(false);
    const [newComment, setNewComment] = React.useState("");
    const [commentAttachment, setCommentAttachment] = React.useState(null);
    const [commentUploadingImage, setCommentUploadingImage] = React.useState(false);
    const [replyingTo, setReplyingTo] = React.useState(null);
    const [submittingComment, setSubmittingComment] = React.useState(false);
    const [feedbackRating, setFeedbackRating] = React.useState(0);
    const [feedbackComment, setFeedbackComment] = React.useState("");
    const [submittingFeedback, setSubmittingFeedback] = React.useState(false);
    const [createOpen, setCreateOpen] = React.useState(false);
    const [showFilters, setShowFilters] = React.useState(false);

    const fetchTickets = React.useCallback(() => {
        if (!token) return;
        setLoading(true);
        setError(null);
        const query = {
            page,
            limit: pageSize,
            ...(search && { search }),
            ...(statusFilter && { status: statusFilter }),
            ...(ticketTypeFilter && { ticketType: ticketTypeFilter }),
        };
        getRequest(query, undefined, token, endpoints.partner.helpDeskTickets)
            .then((res) => {
                if (res.data.ticket) {
                    setTicketDetail(res.data.ticket);
                    setComments(res.data.comments || []);
                    setFeedback(res.data.feedback || null);
                } else {
                    setTickets(res.data.tickets);
                }
            })
            .catch((err) => {
                setError(err?.response?.data?.message || "Failed to load tickets");
                notification.error({ message: err?.response?.data?.message });
            })
            .finally(() => setLoading(false));
    }, [token, page, pageSize, search, statusFilter, ticketTypeFilter]);

    const fetchTicketDetail = React.useCallback(() => {
        if (!token || !ticketId) return;
        setDetailLoading(true);
        getRequest({ ticketId }, undefined, token, endpoints.partner.helpDeskTickets)
            .then((res) => {
                setTicketDetail(res.data.ticket);
                setComments(res.data.comments || []);
                setFeedback(res.data.feedback || null);
            })
            .catch((err) => {
                notification.error({ message: err?.response?.data?.message });
            })
            .finally(() => setDetailLoading(false));
    }, [token, ticketId]);

    useEffect(() => {
        if (ticketId) {
            fetchTicketDetail();
        } else {
            fetchTickets();
        }
    }, [ticketId, fetchTicketDetail, fetchTickets]);

    const handleSearch = () => {
        setPage(1);
        setSearchParams({
            page: "1",
            limit: pageSize.toString(),
            ...(search && { search }),
            ...(statusFilter && { status: statusFilter }),
            ...(ticketTypeFilter && { ticketType: ticketTypeFilter }),
        });
    };

    const handleSearchChange = (value) => {
        setPage(1);
        setSearch(value);
    };

    const handleStatusFilterChange = (value) => {
        setPage(1);
        setStatusFilter(value);
    };

    const handleTicketTypeFilterChange = (value) => {
        setPage(1);
        setTicketTypeFilter(value);
    };

    const clearAllFilters = () => {
        setSearch("");
        setStatusFilter("");
        setTicketTypeFilter("");
        setPage(1);
        setSearchParams({ page: "1", limit: String(pageSize) });
    };

    const handleTableChange = (pagination) => {
        setPage(pagination.current);
        setPageSize(pagination.pageSize);
        setSearchParams({
            page: String(pagination.current),
            limit: String(pagination.pageSize),
            ...(search && { search }),
            ...(statusFilter && { status: statusFilter }),
            ...(ticketTypeFilter && { ticketType: ticketTypeFilter }),
        });
    };

    const handleBackToList = () => {
        setSearchParams({ page: String(page), limit: String(pageSize), ...(search && { search }), ...(statusFilter && { status: statusFilter }), ...(ticketTypeFilter && { ticketType: ticketTypeFilter }) });
        setTicketDetail(null);
        setComments([]);
        setFeedback(null);
        fetchTickets();
    };

    const uploadCommentImage = async (file) => {
        const ext = file.name.split(".").pop()?.toLowerCase() || "jpg";
        const key = `help-desk/tickets/${ticketId}/comments/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
        const fileType = file.type || (ext === "png" ? "image/png" : "image/jpeg");
        const { data } = await getRequest({ fileType, key }, undefined, token, endpoints.partner.uploadsUrl);
        await fetch(data.uploadURL, { method: "PUT", headers: { "Content-Type": fileType }, body: file });
        return data.publicURL;
    };

    const handleCommentImageSelect = async (e) => {
        const file = e.target?.files?.[0];
        if (!file || !token || commentAttachment) return;
        if (!file.type.startsWith("image/")) {
            notification.warning({ message: t("helpDesk.imagesOnly") });
            return;
        }
        e.target.value = "";
        setCommentUploadingImage(true);
        try {
            const publicURL = await uploadCommentImage(file);
            setCommentAttachment(publicURL);
        } catch (err) {
            notification.error({ message: err?.response?.data?.message || t("common.error") });
        } finally {
            setCommentUploadingImage(false);
        }
    };

    const handleAddComment = () => {
        if (!newComment.trim() || !ticketId || !token) return;
        setSubmittingComment(true);
        postRequest(
            {
                ticketId,
                content: newComment.trim(),
                ...(replyingTo && { replyTo: replyingTo._id }),
                ...(commentAttachment && { attachments: [commentAttachment] }),
            },
            undefined,
            undefined,
            token,
            endpoints.partner.helpDeskComments
        )
            .then(() => {
                setNewComment("");
                setCommentAttachment(null);
                setReplyingTo(null);
                fetchTicketDetail();
                notification.success({ message: t("helpDesk.commentAdded") });
            })
            .catch((err) => notification.error({ message: err?.response?.data?.message }))
            .finally(() => setSubmittingComment(false));
    };

    const handleSubmitFeedback = () => {
        if (!ticketId || !token) return;
        setSubmittingFeedback(true);
        postRequest({ ticketId, rating: feedbackRating, comment: feedbackComment }, undefined, undefined, token, endpoints.partner.helpDeskFeedback)
            .then(() => {
                setFeedbackRating(0);
                setFeedbackComment("");
                fetchTicketDetail();
                notification.success({ message: t("helpDesk.feedbackThanks") });
            })
            .catch((err) => notification.error({ message: err?.response?.data?.message }))
            .finally(() => setSubmittingFeedback(false));
    };

    const getTicketDescriptor = (ticket) => {
        if (!ticket) return "";
        const type = ticket.ticketType;
        const order = ticket.relevantOrder;
        const product = ticket.relevantProduct;
        const inbound = ticket.relevantInbound;
        const transaction = ticket.relevantTransaction;

        const shortId = (id) => (id ? `…${String(id).slice(-6)}` : "");

        if (type === "Order") {
            const shopify = order?.shopifyOrderNumber;
            const tracking =
                order?.courierTrackingId ||
                order?.bostaTrackingId ||
                order?.shipbluTrackingId ||
                order?.trackingId ||
                order?.externalId;
            if (shopify && tracking) return `${shopify} · ${tracking}`;
            return shopify || tracking || "";
        }

        if (type === "Product") {
            const sku = product?.sku || product?.externalId;
            return sku || shortId(product?._id);
        }

        if (type === "Inventory" || type === "Inbound") {
            return shortId(inbound?._id);
        }

        if (type === "Finance" || type === "Transaction") {
            return shortId(transaction?._id);
        }

        // General Inquiry and any unknown type
        return "";
    };

    const columns = [
        { title: t("helpDesk.subject"), dataIndex: "subject", key: "subject", ellipsis: true },
        { title: t("helpDesk.type"), dataIndex: "ticketType", key: "ticketType", width: 110 },
        {
            title: t("helpDesk.related"),
            dataIndex: "related",
            key: "related",
            width: 160,
            ellipsis: true,
            render: (_, row) => getTicketDescriptor(row) || "-",
        },
        { title: t("common.status"), dataIndex: "status", key: "status", width: 110, render: (s) => <Chip size="small" label={s} /> },
        { title: t("common.date"), dataIndex: "createdAt", key: "createdAt", width: 140, render: (d) => moment(d).format("lll") },
        {
            title: t("common.actions"),
            key: "actions",
            width: 100,
            render: (_, row) => (
                <Button
                    size="small"
                    sx={{ ...main_button, textTransform: "none", py: 0.5, px: 1, fontSize: "0.75rem" }}
                    onClick={() => setSearchParams({ ...Object.fromEntries(searchParams), ticketId: row._id })}
                >
                    {t("common.view")}
                </Button>
            ),
        },
    ];

    const dataSource = tickets?.docs?.map((doc) => ({ ...doc, key: doc._id })) || [];

    if (ticketId && (ticketDetail || detailLoading)) {
        return (
            <Box sx={{ width: "100%", paddingX: isMobile ? 2 : 5 }}>
                <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                    <IconButton onClick={handleBackToList} sx={{ color: "var(--secColor4)" }}>
                        <ArrowBack />
                    </IconButton>
                    <Typography variant="h5" sx={{ color: "var(--secColor4)", fontWeight: "bold" }}>
                        {t("helpDesk.ticketDetail")}
                    </Typography>
                </Box>
                {detailLoading ? (
                    <Box sx={{ ...centered_flex_box, minHeight: 200 }}>
                        <CircularProgress sx={{ color: "var(--primaryColor)" }} />
                    </Box>
                ) : (
                    <>
                        <NewCard
                            sx={{ mb: 2 }}
                            content={
                                <Box>
                                    <Typography variant="h6" sx={{ color: "var(--secColor4)", mb: 1 }}>
                                        {ticketDetail?.subject}
                                    </Typography>
                                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1 }}>
                                        <Chip size="small" label={ticketDetail?.ticketType} />
                                        <Chip size="small" label={ticketDetail?.status} />
                                        <Typography variant="caption" sx={{ color: "var(--textSecondary)", alignSelf: "center" }}>
                                            {moment(ticketDetail?.createdAt).format("lll")}
                                        </Typography>
                                    </Box>
                                    {getTicketDescriptor(ticketDetail) && (
                                        <Typography variant="body2" sx={{ color: "var(--primaryColor)", mb: 0.5 }}>
                                            {getTicketDescriptor(ticketDetail)}
                                        </Typography>
                                    )}
                                    <Typography variant="body2" sx={{ color: "var(--textSecondary)", whiteSpace: "pre-wrap" }}>
                                        {ticketDetail?.description}
                                    </Typography>
                                    {ticketDetail?.attachments && ticketDetail.attachments.length > 0 && (
                                        <Box sx={{ mt: 1, display: "flex", gap: 1, flexWrap: "wrap" }}>
                                            <Image.PreviewGroup>
                                                {ticketDetail.attachments.map((url, idx) => (
                                                    <Image
                                                        key={idx}
                                                        src={url}
                                                        alt={`ticket-attachment-${idx}`}
                                                        style={{ borderRadius: 4 }}
                                                        height={120}
                                                        width={160}
                                                    />
                                                ))}
                                            </Image.PreviewGroup>
                                        </Box>
                                    )}
                                </Box>
                            }
                        />
                        {ticketDetail?.resolutionActions && ["Resolved", "Closed"].includes(ticketDetail?.status) && (
                            <NewCard
                                sx={{ mb: 2 }}
                                content={
                                    <Box>
                                        <Typography variant="subtitle2" sx={{ color: "var(--secColor4)", fontWeight: 600, mb: 1 }}>
                                            {t("helpDesk.resolutionActions")}
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: "var(--textSecondary)", whiteSpace: "pre-wrap" }}>
                                            {ticketDetail.resolutionActions}
                                        </Typography>
                                    </Box>
                                }
                            />
                        )}
                        <Typography variant="subtitle1" sx={{ color: "var(--secColor4)", fontWeight: 600, mb: 1 }}>{t("helpDesk.comments")}</Typography>
                        {(() => {
                            const sorted = [...(comments || [])].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
                            const roots = sorted.filter((c) => !(c.replyTo?._id ?? c.replyTo));
                            const getReplies = (id) => sorted.filter((c) => {
                                const parentId = c.replyTo?._id ?? c.replyTo;
                                return parentId && String(parentId) === String(id);
                            });
                            const CommentBlock = ({ comment, depth }) => {
                                const replies = getReplies(comment._id);
                                const isNested = depth > 0;
                                return (
                                    <Box key={comment._id} sx={{ mb: 1, ...(isNested && { marginInlineStart: { xs: 2, sm: 3 }, borderInlineStart: "2px solid", borderColor: "var(--primaryColor)", pl: { xs: 1.5, sm: 2 }, py: 0.5 }), maxWidth: "100%", boxSizing: "border-box" }}>
                                        <NewCard sx={{ mb: replies.length > 0 ? 1 : 0 }} content={
                                            <Box>
                                                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
                                                    <Typography variant="caption" sx={{ color: "var(--textSecondary)" }}>
                                                        {comment.creator?.name || comment.creator?.email || "Partner"} · {moment(comment.createdAt).format("lll")}
                                                        {comment.creatorType === "Agent" && <Chip size="small" label={t("helpDesk.support")} sx={{ ml: 1 }} />}
                                                        {isNested && <Typography component="span" variant="caption" sx={{ color: "var(--primaryColor)", ml: 0.5 }}>· {t("helpDesk.replied")}</Typography>}
                                                    </Typography>
                                                    {!["Resolved", "Closed"].includes(ticketDetail?.status) && (
                                                        <Button size="small" startIcon={<Reply />} sx={{ ...main_button, textTransform: "none", minWidth: "auto", py: 0.5, px: 1, fontSize: "0.75rem" }} onClick={() => setReplyingTo({ _id: comment._id, content: comment.content })}>
                                                            {t("helpDesk.reply")}
                                                        </Button>
                                                    )}
                                                </Box>
                                                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", mt: 0.5 }}>
                                                    {comment.content}
                                                </Typography>
                                                {comment.attachments && comment.attachments.length > 0 && (
                                                    <Box sx={{ mt: 1, display: "flex", gap: 1, flexWrap: "wrap" }}>
                                                        <Image.PreviewGroup>
                                                            {comment.attachments.map((url, idx) => (
                                                                <Image
                                                                    key={idx}
                                                                    src={url}
                                                                    alt={`comment-attachment-${idx}`}
                                                                    style={{ borderRadius: 4 }}
                                                                    height={100}
                                                                    width={140}
                                                                />
                                                            ))}
                                                        </Image.PreviewGroup>
                                                    </Box>
                                                )}
                                            </Box>
                                        } />
                                        {replies.map((r) => <CommentBlock key={r._id} comment={r} depth={depth + 1} />)}
                                    </Box>
                                );
                            };
                            return roots.map((c) => <CommentBlock key={c._id} comment={c} depth={0} />);
                        })()}
                        {!["Resolved", "Closed"].includes(ticketDetail?.status) && (
                            <NewCard sx={{ mt: 2 }} content={
                                <Box>
                                    {replyingTo && (
                                        <Box sx={{ mb: 1, p: 1, borderRadius: 1, bgcolor: "var(--secColor2)", border: "1px solid var(--borderColor)", display: "flex", alignItems: "flex-start", gap: 1 }}>
                                            <Typography variant="body2" sx={{ color: "var(--textSecondary)", flex: 1, overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>
                                                {t("helpDesk.replyingTo")}: {replyingTo.content}
                                            </Typography>
                                            <IconButton size="small" onClick={() => setReplyingTo(null)} sx={{ flexShrink: 0 }}><Close fontSize="small" /></IconButton>
                                        </Box>
                                    )}
                                    <MainInput
                                        fullWidth
                                        multiline
                                        rows={3}
                                        placeholder={t("helpDesk.addCommentPlaceholder")}
                                        value={newComment}
                                        onChange={(e) => setNewComment(e.target.value)}
                                        sx={{ mb: 1 }}
                                    />
                                    <Typography variant="body2" sx={{ color: "var(--textSecondary)", mb: 1 }}>{t("helpDesk.attachments")} ({commentAttachment ? 1 : 0}/1)</Typography>
                                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center", mb: 1 }}>
                                        {commentAttachment && (
                                            <Box sx={{ position: "relative", width: 72, height: 72 }}>
                                                <img src={commentAttachment} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 8 }} />
                                                <IconButton size="small" onClick={() => setCommentAttachment(null)} sx={{ position: "absolute", top: -8, right: -8, bgcolor: "var(--appBg)", "&:hover": { bgcolor: "var(--cardBg)" }, width: 24, height: 24 }}><Close fontSize="small" /></IconButton>
                                            </Box>
                                        )}
                                        {!commentAttachment && (
                                            <Button component="label" variant="outlined" size="small" disabled={commentUploadingImage} sx={{ minWidth: 72, height: 72, borderStyle: "dashed" }}>
                                                <input type="file" accept="image/*" hidden onChange={handleCommentImageSelect} />
                                                <AddPhotoAlternate sx={{ color: "var(--textSecondary)" }} />
                                            </Button>
                                        )}
                                    </Box>
                                    <Button size="small" sx={{ ...main_button, textTransform: "none", py: 0.5, px: 1, fontSize: "0.75rem" }} onClick={handleAddComment} disabled={!newComment.trim() || submittingComment}>
                                        {submittingComment ? t("common.submitting") : t("helpDesk.addComment")}
                                    </Button>
                                </Box>
                            } />
                        )}
                        {["Resolved", "Closed"].includes(ticketDetail?.status) && !feedback && (
                            <NewCard sx={{ mt: 2 }} content={
                                <Box>
                                    <Typography variant="subtitle2" sx={{ color: "var(--secColor4)", mb: 1 }}>{t("helpDesk.rateExperience")}</Typography>
                                    <Rating value={feedbackRating} onChange={(_, v) => setFeedbackRating(v || 0)} size="large" sx={{ mb: 1 }} />
                                    <MainInput fullWidth multiline rows={2} placeholder={t("helpDesk.feedbackCommentPlaceholder")} value={feedbackComment} onChange={(e) => setFeedbackComment(e.target.value)} sx={{ mb: 1 }} />
                                    <Button size="small" sx={{ ...main_button, textTransform: "none", py: 0.5, px: 1, fontSize: "0.75rem" }} onClick={handleSubmitFeedback} disabled={submittingFeedback}>
                                        {submittingFeedback ? t("common.submitting") : t("helpDesk.submitFeedback")}
                                    </Button>
                                </Box>
                            } />
                        )}
                        {feedback && (
                            <NewCard sx={{ mt: 2 }} content={
                                <Typography variant="body2" sx={{ color: "var(--textSecondary)" }}>
                                    {t("helpDesk.youRated")} {feedback.rating}/5 {feedback.comment && `· ${feedback.comment}`}
                                </Typography>
                            } />
                        )}
                    </>
                )}
            </Box>
        );
    }

    return (
        <div>
            <Box sx={{ width: "100%", paddingX: isMobile ? 2 : 5 }}>
                <Typography variant="h5" sx={{ color: "var(--secColor4)", fontWeight: "bold", mb: 2 }}>
                    {t("helpDesk.title")}
                </Typography>

                <FilterChips
                    value={statusFilter}
                    onChange={handleStatusFilterChange}
                    options={STATUS_OPTIONS.map((o) => [o.value, t(o.labelKey)])}
                />
                <FilterChips
                    value={ticketTypeFilter}
                    onChange={handleTicketTypeFilterChange}
                    options={[['', t("common.all")], ...TICKET_TYPES.map((o) => [o.value, t(o.labelKey)])]}
                />
                <SearchFilterBar
                    placeholder={t("common.search")}
                    value={search}
                    onChange={handleSearchChange}
                    onSearch={handleSearch}
                    isMobile={isMobile}
                    actions={(
                        <button type="button" className="cz-chip red" onClick={() => setCreateOpen(true)}>
                            {t("helpDesk.newTicket")}
                            {plusIcon}
                        </button>
                    )}
                />

                <Box sx={{
                    width: "100%",
                    overflowX: "auto",
                    "& .ant-table-wrapper": { width: "100%" },
                    "& .ant-table": { minWidth: isMobile ? 300 : 800 },
                    "& .ant-table-cell": { whiteSpace: isMobile ? "normal" : "nowrap" },
                }}>
                    <Table
                        columns={columns}
                        dataSource={dataSource}
                        loading={loading}
                        pagination={{
                            current: page,
                            pageSize,
                            total: tickets?.totalDocs || 0,
                            showSizeChanger: true,
                            showTotal: (total) => t("helpDesk.totalTickets", { total }),
                        }}
                        onChange={handleTableChange}
                        locale={{ emptyText: t("common.noData") }}
                        scroll={{ x: isMobile ? 600 : 800 }}
                    />
                </Box>
            </Box>

            <CreateTicketModal
                open={createOpen}
                onClose={() => setCreateOpen(false)}
                token={token}
                context={null}
                onSuccess={() => { setCreateOpen(false); fetchTickets(); }}
            />
        </div>
    );
};

const mapStateToProps = (state) => ({
    token: state?.auth?.token,
});

export default connect(mapStateToProps)(HelpDesk);
